<template>
  <article class="media">
    <div class="media-content">
      <div class="content">
        <div class="columns is-multiline article-header-columns">
          <div class="column is-6-tablet is-2-desktop article-thumb-col" id="col_31">
            <label v-if="article?.printDocuments" class="tag is-light article-thumb-label">
              Full Article, Single Pages
            </label>
            <a
              v-if="article?.printDocuments"
              :href="pdf_page"
              target="_blank"
              class="article-thumb-link"
            >
              <span v-html="imgHtml" />
            </a>
          </div>
          <div class="column is-6-tablet is-2-desktop article-thumb-col" id="col_32">
            <label v-if="article.storyPdfFacing!=null" class="tag is-light article-thumb-label">
              Article Spread(s)
            </label>
              <span
                v-if="article.storyPdfFacing!=null && article?.printDocuments"
                v-html="imgHtml"
                class="article-thumb-link"
                @click="openPdf(article.storyPdfFacing)"
              />
          </div>
          <div class="column is-12-tablet is-8-desktop article-meta-col">
            <h1 v-if="printData" class="title is-3 has-text-centered">{{ publicationName }}</h1>
            <p v-if="printData" class="subtitle is-6 has-text-centered">{{ formattedCoverDate }}</p>
            <h2 v-if="article" class="subtitle is-4 has-text-centered">{{ article.storyName }}</h2>
            <p v-if="dekSubtitle" class="subtitle is-6">{{ dekSubtitle }}</p>
            <IssueArticleByline
              v-if="useUdfByline"
              :authors="udfAuthors"
            />
            <IssueArticleByline
              v-else-if="hasXmlDocument"
              :authors="[]"
              :xml-byline="xmlByline"
            />
            <p class="article-meta-line">
              <span><strong>Page: </strong>{{ article.article_coverPage }}{{ pageNumberList }}</span>
              <strong class="issue-article-word-count">{{ wordCountLabel }}</strong>
            </p>
            <p v-if="article.section"><strong>Section: </strong> {{ article.section }} </p>
          </div>
        </div>
      </div><hr>
    </div>
  </article>
</template>

<script>
import IssueArticleByline from '@/components/layout/issue/article/IssueArticleByline.vue'
import { formatCoverDate } from '@/utils/formatCoverDate'

export default {
  name: 'IssueArticleHeader',
  components: {
    IssueArticleByline
  },
  props: {
    article: {
      type: Object,
      required: true
    },
    publicationName: {
      type: String,
      required: true
    },
    coverDisplayDate: {
      type: String,
      required: true
    },
    xml_summary: {
      type: String,
      default: ''
    },
    xmlByline: {
      type: String,
      default: ''
    },
    wordCount: {
      type: Number,
      default: null
    }
  },
  computed: {
    printData () {
      return this.article?.printDocuments?.[0] ?? null
    },
    dekSubtitle () {
      const d = this.printData
      if (!d) return ''
      if (d.documentDek) return d.documentDek
      if (d.udf && d.udf.summary) return d.udf.summary
      if (d.xml && this.xml_summary) return this.xml_summary
      return ''
    },
    udfAuthors () {
      return this.printData?.documentAuthors ?? []
    },
    useUdfByline () {
      return !!(this.printData && this.printData.udf && this.udfAuthors.length)
    },
    hasXmlDocument () {
      return !!(this.printData && this.printData.xml)
    },
    pageNumberList () {
      const nums = this.printData?.pageNumbers ?? []
      return (Array.isArray(nums) ? nums : []).filter((x) => x != null).join(',')
    },
    thumb () {
      return (
        this.article?.printDocuments?.[0]?.thumbnail?.mediaSignedUrls ??
        null
      )
    },
    pdf_page () {
      return (
        this.article?.printDocuments?.[0]?.articlePdf?.mediaSignedUrls?.awsS3Download ??
        null
      )
    },
    imgHtml () {
      // const sizeClass = this.article.article_pdf_spread ? 'is-96x96' : 'is-128x128'
      const sizeClass = 'is-128x128' //'is-96x96'
      if (!this.thumb?.awsS3Download) {
        return (
          '<figure class="image ' + sizeClass + ' article-thumb-frame article-thumb-frame--empty">' +
          '<span class="article-thumb-placeholder">No thumbnail</span>' +
          '</figure>'
        )
      }
      return (
        '<figure class="image ' + sizeClass + '">' +
        '<img alt="Thumbnail" src="' + this.thumb.awsS3Download + '">' +
        '</figure>'
      )
    },
    wordCountLabel () {
      if (this.wordCount == null || Number.isNaN(this.wordCount)) return ''
      return 'Words: ' + this.wordCount
    },
    formattedCoverDate () {
      return formatCoverDate(this.coverDisplayDate)
    }
  },
  methods: {
    async openPdf (assetId) {
      if (!assetId?.[0]) return
      try {
        const response = await this.$http.post(
          this.imageTokenUrl,
          {
            id: assetId[0],
            origurl: true
          },
          {
            headers: {
              Authorization: 'Bearer '+this.imageTokenApiBearerToken
            }
          }
        )

        const { originalUrl } = response.data
        if (originalUrl) {
          window.open(originalUrl, '_blank')
        }
      } catch (err) {
        console.error(err)
      }
    }
  }
}
</script>

<style scoped>
  .article-header-columns {
    margin-left: 0;
    margin-right: 0;
  }

  .article-thumb-col,
  .article-meta-col {
    min-width: 0;
  }

  .article-thumb-label {
    display: inline-block;
    margin-bottom: 0.5rem;
    white-space: normal;
    height: auto;
  }

  .article-thumb-link {
    display: inline-block;
    max-width: 100%;
    cursor: pointer;
  }

  .article-thumb-link :deep(figure) {
    position: static !important;
    width: 100%;
    max-width: 128px;
    height: auto !important;
    margin: 0 0 0.5rem 0 !important;
  }

  .article-thumb-link :deep(img) {
    display: block;
    width: 100%;
    max-width: 100%;
    height: auto;
  }

  .article-thumb-link :deep(.article-thumb-frame--empty) {
    width: 128px !important;
    height: 128px !important;
    max-width: 100%;
    border: 2px solid #363636;
    box-sizing: border-box;
    overflow: hidden;
  }

  .article-thumb-link :deep(.article-thumb-placeholder) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 8px;
    font-size: 18px;
    font-weight: 700;
    text-align: center;
    line-height: 1.2;
    color: #363636;
  }

  .article-meta-line {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 0.5rem 1.5rem;
    margin: 0;
  }

  .article-meta-col .title,
  .article-meta-col .subtitle {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  @media screen and (max-width: 768px) {
    .article-meta-col .title.is-3 {
      font-size: 1.5rem;
    }
    .article-meta-col .subtitle.is-4 {
      font-size: 1.15rem;
    }
  }
</style>
